import { useState } from 'react';
import {
  escalationTest,
  facilityEscalation,
  facilityEscalationSave,
  type ApiError,
  type EscalationSettings,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, LoadError, pre, sx, useApi, useGuarded, usePage } from './kit';
import { BuildingLoading, PickOneBuilding, useBuilding, type BuildingPick } from './building';

// Settings, Alert ladder (Console V5; design T10736-10830, alLayer V5 L37920-37970; spec 5,
// decided 2026-09-29): who gets paged, in what order, per urgency, within fixed limits.
// A Facility Admin edits their own building's plan (GET /organizations/:id/escalation, PUT
// .../escalation-settings, router.ts): time per step, the CRITICAL re-page and the 911
// policy; the server refuses anything outside the limits and audits every save. Send a test
// page is POST /escalation/test. Super Admin and Operator with one building picked in the
// facility switcher edit that building's plan the same way (the server lets Aithora manage any
// building, by id); with a region or several buildings picked the section asks to pick one;
// with nothing picked they see the platform defaults the server starts every building from
// (paging-plan.ts), and editing those is not built yet, which the section says. The channel
// per step and the shift are not stored by the server yet either: the chips show the
// design's defaults and say NOT BUILT YET.

type Urg = 'CRITICAL' | 'HIGH' | 'MEDIUM';
type Shift = 'Day' | 'Evening' | 'Night';
const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const chip = (sel: boolean, danger = false) => 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (sel ? 'color:#fff;background:' + (danger ? '#B3261E' : '#264C7E') + ';' : 'color:#3C454F;border:1px solid rgba(34,39,46,.16);');
const STEP_BTN = 'display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:8px;border:1px solid rgba(34,39,46,.2);font-size:18px;font-weight:700;color:#22272E;text-decoration:none;';
const BOX = 'display:flex;flex-direction:column;gap:6px;padding:10px 12px;border:1px solid rgba(34,39,46,.12);border-radius:10px;';
const SCOPE = 'white-space:nowrap;font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.07);border-radius:4px;padding:2px 7px;';
const SH: Record<Shift, string> = { Day: '7 AM to 3 PM', Evening: '3 PM to 11 PM', Night: '11 PM to 7 AM' };
const COVERS: Record<Urg, string> = {
  CRITICAL: 'Falls with injury, self-harm and crisis language, medical crisis.',
  HIGH: 'Falls without injury, vitals out of range, a wandering exit.',
  MEDIUM: 'Missed medications and care tasks, Atlas requests.',
};
const KEY: Record<Urg, 'criticalStepSec' | 'highStepSec' | 'mediumStepSec'> = { CRITICAL: 'criticalStepSec', HIGH: 'highStepSec', MEDIUM: 'mediumStepSec' };
/** The server's defaults and limits (paging-plan.ts STEP_LIMITS, REPAGE_LIMITS): what every building starts from. */
const DEFAULTS: EscalationSettings = {
  criticalStepSec: 60, highStepSec: 120, mediumStepSec: 300, repageSec: 600, criticalCallPolicy: 'flag', highAdminAfterSec: 900, maxSecondsTo911: 300,
  limits: { steps: { CRITICAL: { min: 30, max: 120, def: 60 }, HIGH: { min: 60, max: 300, def: 120 }, MEDIUM: { min: 120, max: 900, def: 300 } }, repage: { min: 300, max: 1800, def: 600 } },
};
/** One tap moves this far (the design's LIM step). */
const STEP: Record<Urg, number> = { CRITICAL: 10, HIGH: 30, MEDIUM: 60 };
const LOCKS = [
  'Turn off the 911 step for CRITICAL, or push it past 5 minutes from the first page.',
  'Downgrade a self-harm or crisis alert. Those are CRITICAL.',
  'Leave a step empty. An empty step falls through to the charge nurse, then everyone.',
  'Send abuse or neglect signals to a staff member named in them.',
];
const hm = (d: Date | string) => new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export function AlertLadderSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (building.mode === 'narrowed') return <PickOneBuilding what="Alert ladder" />;
  // Keyed by the building, so nothing from one building's plan is ever saved to another.
  return <Ladder key={building.orgId ?? 'defaults'} building={building} />;
}

function Ladder({ building }: { building: BuildingPick }) {
  const { client, session } = useSession();
  const { lens } = usePage();
  const { wr } = useGuarded();
  const isFA = lens.lens === 'Facility Admin';
  // The one building this plan is about: the Facility Admin's own, or the one Aithora picked.
  const orgId = building.orgId;
  const plan = useApi((c, s) => facilityEscalation(c, { params: { id: orgId! }, signal: s }), [orgId], !!orgId);
  const [shift, setShift] = useState<Shift>('Night');
  const [urg, setUrg] = useState<Urg>('CRITICAL');
  const [log, setLog] = useState<string[]>([]);
  const [test, setTest] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);

  if (orgId && !plan.data) return plan.error ? <LoadError error={plan.error} onRetry={plan.reload} /> : <Skeleton rows={6} label="Loading the alert ladder" />;
  const live = !!orgId && !!plan.data;
  const st: EscalationSettings = live ? { ...DEFAULTS, ...(plan.data!.settings as Partial<EscalationSettings>) } : DEFAULTS;
  const lim = st.limits?.steps?.[urg] ?? DEFAULTS.limits.steps[urg];
  const secs = st[KEY[urg]];
  const minutes = urg === 'MEDIUM';
  const sl = minutes ? Math.round(secs / 60) + ' min' : secs + ' s';
  const rep = Math.round(st.repageSec / 60);
  const me = isFA ? 'you' : (session?.user.name ?? 'Aithora').split(/\s+/)[0];

  const save = async (body: Partial<Pick<EscalationSettings, 'criticalStepSec' | 'highStepSec' | 'mediumStepSec' | 'repageSec' | 'criticalCallPolicy'>>, line: string) => {
    if (!live || !orgId) return;
    setErr(null);
    try {
      const r = await facilityEscalationSave(client, { params: { id: orgId }, body });
      plan.setData((p) => (p ? { ...p, settings: r.settings as unknown as Record<string, unknown> } : p));
      setLog((l) => [line + ' · ' + me + ' · ' + hm(new Date()), ...l]);
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const setSec = (d: number) => {
    const n = Math.max(lim.min, Math.min(lim.max, secs + d * STEP[urg]));
    if (n === secs) return;
    void save({ [KEY[urg]]: n }, urg + ' time per step: ' + (minutes ? Math.round(n / 60) + ' min' : n + ' s'));
  };
  const setRep = (d: number) => {
    const L = st.limits?.repage ?? DEFAULTS.limits.repage;
    const n = Math.max(L.min, Math.min(L.max, st.repageSec + d * 300));
    if (n === st.repageSec) return;
    void save({ repageSec: n }, 'Re-page: every ' + Math.round(n / 60) + ' min');
  };
  const set911 = (v: 'flag' | 'prompt', label: string) => {
    if (v === st.criticalCallPolicy) return;
    void save({ criticalCallPolicy: v }, '911 policy: ' + label.toLowerCase());
  };
  const sendTest = async () => {
    setErr(null);
    try {
      const r = await escalationTest(client, { body: {} });
      setTest('Sent at ' + hm(r.sentAt) + '. Rang ' + (r.target.name ?? 'the person on call') + (r.live ? '' : ' (' + (r.reason ?? 'not delivered') + ')') + '. Logged.');
      setLog((l) => ['Test page sent · ' + me + ' · ' + hm(r.sentAt), ...l]);
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  // The channels each step uses: the paging plan's own (paging-plan.ts), shown as labels.
  // Nothing on the server saves a channel choice, so they are not buttons.
  const chans = (voiceLocked: boolean) => (
    <span data-testid="ladder-channels" style={sx('display:contents;')}>
      <span title="Push and in-app are always on." style={sx(chip(true) + 'cursor:default;')}>App</span>
      <span title="Set by the paging plan." style={sx(chip(false) + 'cursor:default;')}>SMS</span>
      <span title={voiceLocked ? 'For CRITICAL, voice is always on from step 2.' : 'Set by the paging plan.'} style={sx(chip(voiceLocked) + 'cursor:default;')}>Voice</span>
    </span>
  );
  const steps: { n: string; who: string; sub: string; voice: boolean }[] = {
    CRITICAL: [
      { n: 'Step 1', who: 'The resident’s caregiver, aide + charge nurse on shift + the Facility Admin', sub: 'By role, ' + shift.toLowerCase() + ' shift. ' + sl + ' to acknowledge.', voice: false },
      { n: 'Step 2', who: 'Everyone on shift + the Facility Admin', sub: 'If step 1 has not acknowledged after ' + sl + '.', voice: true },
      { n: 'Step 3', who: '911 flag', sub: (st.criticalCallPolicy === 'flag' ? 'Flagged for staff to call' : 'A named person is prompted to call') + ', then re-pages every ' + rep + ' min until resolved.', voice: true },
    ],
    HIGH: [
      { n: 'Step 1', who: 'The resident’s caregiver, aide + charge nurse on shift + the Facility Admin', sub: sl + ' to acknowledge.', voice: false },
      { n: 'Step 2', who: 'Everyone on shift + the Facility Admin', sub: 'If step 1 has not acknowledged after ' + sl + '.', voice: false },
      { n: '15 min', who: 'Director of nursing', sub: 'If it is still not resolved. HIGH never reaches the 911 flag.', voice: false },
    ],
    MEDIUM: [
      { n: 'Step 1', who: 'Aide on the resident’s wing', sub: sl + ' to acknowledge.', voice: false },
      { n: 'Step 2', who: 'Charge nurse', sub: 'It stops here and never pages everyone. Held during quiet hours.', voice: false },
    ],
  }[urg];
  const flagAt = 2 * st.criticalStepSec;
  const fm = Math.floor(flagAt / 60);
  const fs = flagAt % 60;
  const range = minutes ? Math.round(lim.min / 60) + ' to ' + Math.round(lim.max / 60) + ' minutes'
    : urg === 'CRITICAL' ? lim.min + ' to ' + lim.max + ' seconds. 911 flag at about ' + (fm ? fm + 'm ' : '') + (fs ? fs + 's' : '') + ' from the first page.' : lim.min + ' to ' + lim.max + ' seconds';
  const ctl = (fn: () => void) => (live ? wr(fn) : { onClick: pre, 'aria-disabled': true as const, title: 'Editing the platform defaults is not built yet.' });

  return (
    <div data-screen-label="Alert ladder" data-testid="settings-alert-ladder" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;display:flex;flex-direction:column;gap:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#B3261E;flex:0 0 7px;')} />
        <span style={sx(CAPT)}>Alert ladder · who gets paged, in what order</span>
        <span style={sx('flex:1 1 auto;')} />
        {!live && <span style={sx(SCOPE)}>EDITING NOT BUILT YET</span>}
        <span style={sx(SCOPE)}>{isFA ? (session?.user.orgName ?? 'YOUR BUILDING').toUpperCase() : building.mode === 'picked' ? building.name.toUpperCase() : 'PLATFORM DEFAULTS'}</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;text-wrap:pretty;')}>
        {isFA
          ? 'The named person first, wider only when nobody answers. Changes apply to your building right away and are logged with who and when. Paging Aithora when nobody claims an alert is always on.'
          : building.mode === 'picked'
            ? 'The named person first, wider only when nobody answers. Changes apply to ' + building.name + ' right away and are logged with who and when. Paging Aithora when nobody claims an alert is always on.'
            : 'The defaults every building starts from. Each facility admin sets their own within these limits. Paging Aithora when nobody claims an alert is always on.'}
      </div>
      <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
        <span style={sx(CAPT + 'margin-right:4px;')}>Shift</span>
        {(Object.keys(SH) as Shift[]).map((k) => (
          <a key={k} href="#" onClick={(e) => { pre(e); setShift(k); }} style={sx(chip(shift === k))}>{k + ' · ' + SH[k]}</a>
        ))}
        <span style={sx('flex:1 1 auto;')} />
        <a href="#" onClick={pre} aria-disabled="true" title="Overrides are not built here yet." style={sx('font-size:16px;font-weight:600;color:#2F5C97;')}>Add a one-off override</a>
      </div>
      <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
        <span style={sx(CAPT + 'margin-right:4px;')}>Urgency</span>
        {/* A view switch: the chip on shows that urgency's ladder (pressing it again keeps it). */}
        <span role="tablist" aria-label="Urgency" style={sx('display:contents;')}>
          {(['CRITICAL', 'HIGH', 'MEDIUM'] as Urg[]).map((k) => (
            <a key={k} href="#" role="tab" aria-selected={urg === k} onClick={(e) => { pre(e); setUrg(k); }} style={sx(chip(urg === k, k === 'CRITICAL') + (urg === k ? 'cursor:default;' : ''))}>{k}</a>
          ))}
        </span>
        <span style={sx('flex:1 1 auto;')} />
        <span style={sx(SCOPE)}>CHANNEL CHOICES NOT BUILT YET</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;')}>{COVERS[urg] + ' Voice and SMS skip a number with no consent on file, record why, and the ladder keeps climbing.'}</div>
      <div style={sx('display:flex;flex-direction:column;')}>
        {steps.map((x) => (
          <div key={x.n} style={sx('display:flex;flex-wrap:wrap;align-items:center;gap:8px 14px;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);')}>
            <span style={sx('flex:0 0 56px;font-size:16px;font-weight:700;color:#54606C;')}>{x.n}</span>
            <span style={sx('flex:1 1 220px;min-width:0;')}>
              <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{x.who}</span>
              <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;text-wrap:pretty;')}>{x.sub}</span>
            </span>
            <span style={sx('display:flex;gap:4px;flex-wrap:wrap;justify-content:flex-end;')}>{chans(x.voice && urg === 'CRITICAL')}</span>
          </div>
        ))}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px 18px;')}>
        <div style={sx(BOX)}>
          <span style={sx(CAPT)}>Time per step</span>
          <span style={sx('display:flex;align-items:center;gap:8px;')}>
            <a href="#" {...ctl(() => setSec(-1))} aria-label="Less time" style={sx(STEP_BTN)}>−</a>
            <span style={sx('font-size:20px;font-weight:700;font-variant-numeric:tabular-nums;min-width:72px;text-align:center;')}>{sl}</span>
            <a href="#" {...ctl(() => setSec(1))} aria-label="More time" style={sx(STEP_BTN)}>+</a>
          </span>
          <span style={sx('font-size:16px;color:#54606C;')}>{range}</span>
        </div>
        {urg === 'CRITICAL' && (
          <div style={sx(BOX)}>
            <span style={sx(CAPT)}>Re-page until resolved</span>
            <span style={sx('display:flex;align-items:center;gap:8px;')}>
              <a href="#" {...ctl(() => setRep(-1))} aria-label="Sooner" style={sx(STEP_BTN)}>−</a>
              <span style={sx('font-size:20px;font-weight:700;font-variant-numeric:tabular-nums;min-width:72px;text-align:center;')}>{rep + ' min'}</span>
              <a href="#" {...ctl(() => setRep(1))} aria-label="Later" style={sx(STEP_BTN)}>+</a>
            </span>
            <span style={sx('font-size:16px;color:#54606C;')}>Acknowledged is not resolved. 5 to 30 minutes.</span>
          </div>
        )}
        {urg === 'CRITICAL' && (
          <div style={sx(BOX)}>
            <span style={sx(CAPT)}>911 policy</span>
            <span style={sx('display:flex;gap:4px;flex-wrap:wrap;')}>
              {([['flag', 'Flag it for staff to call'], ['prompt', 'Prompt a named person']] as const).map(([v, label]) => (
                <a key={v} href="#" {...ctl(() => set911(v, label))} style={sx(chip(st.criticalCallPolicy === v))}>{label}</a>
              ))}
            </span>
            <span style={sx('font-size:16px;color:#54606C;')}>{st.criticalCallPolicy === 'flag' ? 'Default. Staff on shift see the flag and make the call.' : 'The charge nurse on shift is prompted to call 911.'}</span>
          </div>
        )}
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
        <span style={sx(CAPT)}>What no setting can do</span>
        {LOCKS.map((l) => (
          <div key={l} style={sx('display:flex;align-items:center;gap:8px;font-size:16px;color:#3C454F;padding:3px 0;')}>
            <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.07);border-radius:4px;padding:1px 6px;')}>LOCKED</span>
            {l}
          </div>
        ))}
      </div>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding-top:10px;border-top:1px solid rgba(34,39,46,.1);')}>
        <span style={sx(CAPT)}>Test page</span>
        <span style={sx('font-size:16px;color:#54606C;flex:1 1 240px;')}>{test ?? 'Runs weekly on its own, Mondays at 10 AM, and on demand. Each result is logged.'}</span>
        <a href="#" {...(isFA ? wr(() => void sendTest()) : { onClick: pre, 'aria-disabled': true as const, title: building.mode === 'picked' ? 'The building’s Facility Admin sends its test page.' : 'A test page rings one building: open it from that building.' })} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;color:#fff;background:#264C7E;white-space:nowrap;')}>Send a test page</a>
      </div>
      <ErrLine error={err} style="margin-top:0;" />
      <div style={sx('display:flex;flex-direction:column;gap:2px;')}>
        <span style={sx(CAPT)}>Changes</span>
        {log.map((g, i) => <div key={i} style={sx('font-size:16px;color:#3C454F;padding:3px 0;')}>{g}</div>)}
        {log.length === 0 && <div style={sx('font-size:16px;color:#54606C;')}>No changes saved here yet. Every save records who changed what, and when.</div>}
      </div>
    </div>
  );
}
